/*
 * Handing a food, a recipe or a meal to somebody else as a link — the nutrition spec, Sharing.
 *
 * The wire itself lives in `@gymcloud/nutrition/share`, which the Android port mirrors and the
 * spec pins. This is the half either app has to write for itself: turning what the app holds
 * into a share, turning a share back into something the app can show, and handing the link to
 * whatever the browser shares with.
 */

import {
  SHARE_LIMIT,
  SHARE_VERSION,
  encodeShare,
  isEntryShare,
  membersOf,
  nutrientPlaces,
  referencesOf,
  shareNutrients,
  type Shared,
  type ShareAmount,
  type ShareCopy,
  type SharedEntry,
  type ShareMember,
} from '@gymcloud/nutrition/share'

import { deckOf } from '@gymcloud/food-marks'

import { foodEmoji, mealEmoji, PLAIN_FOOD_EMOJI } from '~/data/food-emoji'

import {
  EMPTY_NUTRIENTS,
  NUTRIENTS,
  NUTRIENT_LABELS,
  amountSaid,
  macroLine,
  EMPTY_MEAL_STATE,
  mealTotal,
  membersOn,
  nutrientsFor,
  recipeNameFrom,
  summed,
  type Amount,
  type Entry,
  type Food,
  type Ingredient,
  type Meal,
  type Nutrient,
  type Nutrients,
  type Recipe,
} from '~/data/nutrition'

export { SHARE_LIMIT, SHARE_VERSION }

/* ---- What the app shares. ---- */

/** What stands for a food everybody is offered anyway, or null where nobody else is. */
export function referenceOf(food: Food): string | null {
  if (food.source !== 'library') {
    return null
  }
  return food.libraryCode === null ? null : `l:${food.libraryCode}`
}

function shareAmountOf(amount: Amount): ShareAmount {
  return amount.unit === 'portion' && amount.portion !== null
    ? [amount.value, amount.unit, amount.portion]
    : [amount.value, amount.unit]
}

/** The amount a member is offered in. */
export function amountFromShare(amount: ShareAmount): Amount {
  return { value: amount[0], unit: amount[1], portion: amount[2] ?? null }
}

function nutrientsToShare(nutrients: Nutrients) {
  return shareNutrients(NUTRIENTS.map((name) => nutrients[name]))
}

/** And back: the nutrients a link states, whatever tail it left off. */
export function nutrientsFromShare(places: readonly (number | null)[]): Nutrients {
  const eight = nutrientPlaces(places)
  const read: Record<string, number | null> = { ...EMPTY_NUTRIENTS }
  NUTRIENTS.forEach((name, index) => {
    read[name] = eight[index] ?? null
  })
  return { ...(read as unknown as Nutrients), calories: eight[0] ?? 0 }
}

/** A food written out whole, as a share carries one nobody else is offered. */
export function copyOf(food: Food): ShareCopy {
  return {
    n: food.name,
    rf: food.reference,
    nu: nutrientsToShare(food.nutrients),
    ...(food.brand === null ? {} : { b: food.brand }),
    ...(food.barcode === null ? {} : { bc: food.barcode }),
    ...(food.portions.length === 0
      ? {}
      : {
          p: food.portions.map((portion) => [portion.name, portion.amount, portion.unit] as const),
        }),
    ...(food.density === null ? {} : { dn: food.density }),
  }
}

/**
 * And back: the food a copy stands for, which is nobody's own until it is saved. A share
 * carries no provenance and gives none: where a food came from is where it entered an
 * account, and a food arriving by link entered this one from whoever sent it.
 */
export function foodFromShare(copy: ShareCopy): Food {
  return {
    id: null,
    source: 'own',
    library: null,
    libraryCode: null,
    broughtFrom: null,
    density: copy.dn ?? null,
    name: copy.n,
    emoji: null,
    brand: copy.b ?? null,
    barcode: copy.bc ?? null,
    reference: copy.rf,
    nutrients: nutrientsFromShare(copy.nu),
    portions: (copy.p ?? []).map(([name, amount, unit]) => ({ name, amount, unit })),
    recipe: null,
  }
}

/** A food as a member of a share: a reference where everybody is offered it, a copy otherwise. */
export function shareMemberOf(
  food: Food | null,
  name: string,
  amount: Amount,
  comesTo: Nutrients,
): ShareMember {
  const carried = shareAmountOf(amount)
  if (food === null) {
    // A quick entry: a name and what it came to, with no food behind it.
    return { a: carried, n: name, nu: nutrientsToShare(comesTo) }
  }
  const reference = referenceOf(food)
  return reference === null ? { a: carried, c: copyOf(food) } : { a: carried, r: reference }
}

function memberFromIngredient(ingredient: Ingredient): ShareMember {
  return shareMemberOf(ingredient.food, ingredient.name, ingredient.amount, ingredient.nutrients)
}

/**
 * What an entry share carries beyond its members: the moment it lands on, and what it goes by.
 *
 * The moment is the whole difference between sending and sharing. A blueprint has no moment —
 * it is a thing, not an occasion — and an entry is nothing else: 320 g of chicken at eight in
 * the evening. Carrying it is why a plate made at dinner and opened at breakfast still lands
 * on dinner.
 */
export interface SentAt {
  readonly day: string
  readonly time: string
}

/*
 * The emoji a link carries is always the one the app draws in front — a food's face, a meal's
 * or a recipe's deck — worked out here rather than left to the reader: a link is read by a chat
 * that knows nothing about our table, so what it carries has to be the answer and not the
 * question. Nobody reads it back for more than saying it in text.
 */
function sentShell(
  at: SentAt,
  name: string,
  emoji: string,
  by: string | null,
): Omit<SharedEntry, 'm'> {
  return {
    v: SHARE_VERSION,
    k: 'entry',
    ...(by === null ? {} : { by }),
    n: name,
    e: emoji,
    dy: at.day,
    tm: at.time,
  }
}

/**
 * Entries chosen on a day, sent to somebody to log — the nutrition spec, Sending from the app.
 * Whatever was chosen and however much of it: one entry is an entry share of one member, and
 * the same food chosen twice is two members in the amounts each was logged in.
 */
export function sentFromEntries(entries: readonly Entry[], by: string | null, at: SentAt): Shared {
  const name = recipeNameFrom(entries.map((entry) => entry.name))
  const parts = entries.map((entry) => ({ name: entry.name, calories: entry.nutrients.calories }))
  return {
    ...sentShell(at, name, deckOf(name, parts)[0]?.emoji ?? PLAIN_FOOD_EMOJI, by),
    m: entries.map((entry) => shareMemberOf(entry.food, entry.name, entry.amount, entry.nutrients)),
  }
}

/**
 * A meal's page sent as entries — from a meal of the account's own, from one being made, and
 * from the page that exists only to make one for somebody else. It lends the entry share its
 * name and the emoji of its deck, since it has both and what it holds is what is being sent.
 */
export function sentFromMeal(meal: Meal, by: string | null, at: SentAt): Shared {
  const members = membersOn(meal.slots, meal.state)
  const name =
    meal.name.trim() === '' ? recipeNameFrom(members.map((each) => each.name)) : meal.name
  return {
    ...sentShell(at, name, mealEmoji({ ...meal, name }), by),
    m: members.map(memberFromIngredient),
  }
}

/** A food or a recipe sent as one entry, in the amount standing on the page it is sent from. */
export function sentFromFood(
  food: Food,
  amount: Amount,
  comesTo: Nutrients,
  by: string | null,
  at: SentAt,
): Shared {
  return {
    ...sentShell(at, food.name, foodEmoji(food), by),
    m: [shareMemberOf(food, food.name, amount, comesTo)],
  }
}

/** A meal of the account's own, shared as a blueprint, as it stands. */
export function sharedFromMeal(meal: Meal, by: string | null): Shared {
  const members = membersOn(meal.slots, meal.state)
  /*
   * Named after what it holds where it has no name of its own. A meal is shared whether it has
   * been saved or not — the nutrition spec, Sharing from the app — and one being made starts
   * nameless, so writing the blank would put a link on the wire that neither reader will open:
   * both refuse a share that says nothing it goes by.
   */
  const name =
    meal.name.trim() === '' ? recipeNameFrom(members.map((each) => each.name)) : meal.name
  return {
    v: SHARE_VERSION,
    k: 'meal',
    ...(by === null ? {} : { by }),
    n: name,
    e: mealEmoji({ ...meal, name }),
    m: members.map(memberFromIngredient),
  }
}

/**
 * A food shared as a blueprint, in the amount its page opens on. A recipe is shared as a
 * recipe — its ingredients, each shared the way any food is, and the servings the whole makes.
 */
export function sharedFromFood(food: Food, amount: Amount, by: string | null): Shared {
  const carriedBy = by === null ? {} : { by }
  const wearing = { e: foodEmoji(food) }
  if (food.recipe !== null) {
    return {
      v: SHARE_VERSION,
      k: 'recipe',
      ...carriedBy,
      n: food.name,
      ...wearing,
      sv: food.recipe.servings,
      // The weight travels where there is one, so a recipe arrives able to be logged by weight
      // the way it was where it was written.
      ...(food.recipe.totalGrams != null && food.recipe.totalGrams > 0
        ? { tw: food.recipe.totalGrams }
        : {}),
      i: food.recipe.ingredients.map(memberFromIngredient),
    }
  }
  return {
    v: SHARE_VERSION,
    k: 'food',
    ...carriedBy,
    n: food.name,
    ...wearing,
    f: shareMemberOf(food, food.name, amount, food.nutrients),
  }
}

/**
 * What a share is called. Every kind says it outright now, so a library food shared as a bare
 * reference still has a name before anything has been read from the server.
 */
export function sharedName(shared: Shared): string {
  return shared.n
}

/* ---- Handing it over. ---- */

/** Where a link written here points: this app's own address. */
export function shareOrigin(): string {
  return window.location.origin
}

export type ShareOutcome = 'shared' | 'copied' | 'too-large' | 'failed'

/**
 * The link handed to whatever the browser shares with, and copied to the clipboard where the
 * browser shares with nothing — the nutrition spec, Sharing. A share cancelled by the person
 * is not a failure and says nothing: they closed the sheet on purpose.
 */
export async function handOver(
  shared: Shared,
  said: SharedSaid,
  origin: string = shareOrigin(),
): Promise<ShareOutcome> {
  const link = `${origin.replace(/\/$/, '')}/shared?d=${await encodeShare(shared)}`
  if (link.length > SHARE_LIMIT) {
    return 'too-large'
  }
  const text = `${said.heading}\n${said.figures}\n${link}`
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({
        title: said.heading,
        text: `${said.heading}\n${said.figures}`,
        url: link,
      })
      return 'shared'
    } catch (error) {
      // Closing the sheet aborts the promise; only a real failure falls through to copying.
      if (error instanceof DOMException && error.name === 'AbortError') {
        return 'shared'
      }
    }
  }
  try {
    await navigator.clipboard.writeText(text)
    return 'copied'
  } catch {
    return 'failed'
  }
}

/** The two lines a message says over the link — the nutrition spec, Sharing. */
export interface SharedSaid {
  /** Who shared it and what it is called, under its emoji. */
  readonly heading: string
  /** What it comes to, and the one thing worth saying about the kind of thing it is. */
  readonly figures: string
}

/**
 * What the message says over the link: who handed it over and what it is called, then what it
 * comes to — the nutrition spec, Handing it over.
 *
 * The figures are the line the app already writes under every entry, so a message reads like
 * the app rather than like a second dialect of it. What follows them is the one thing worth
 * saying about this kind of thing and nothing else: how many things an entry share or a meal
 * holds, that a recipe's figures are a serving of it, and how much of a food is being offered.
 *
 * The verb is the whole point of the split, so the message carries it: entries are *sent you*,
 * because the thing to do with them is log them, and a blueprint is *shared*, because the
 * thing to do with it is keep it.
 */
export function saidOf(
  shared: Shared,
  comesTo: Nutrients | null,
  by: string | null = null,
): SharedSaid {
  const emoji = shared.e ?? ''
  const name = emoji === '' ? shared.n : `${emoji} ${shared.n}`
  const held = membersOf(shared).length
  const tail =
    shared.k === 'entry' || shared.k === 'meal'
      ? `${String(held)} item${held === 1 ? '' : 's'}`
      : shared.k === 'recipe'
        ? `per serving, makes ${String(shared.sv)}`
        : amountSaid(amountFromShare(shared.f.a))
  const verb = isEntryShare(shared) ? 'sent you' : 'shared'
  return {
    // Without a name to say it with, the verb has nobody to be about and the name stands alone.
    heading: by === null || by.trim() === '' ? name : `${by} ${verb} ${name}`,
    figures: comesTo === null ? tail : `${macroLine(comesTo)} · ${tail}`,
  }
}

/* ---- What a link opens on. ---- */

/** A member with the food its reference named, where it named one. */
export interface ReadMember {
  readonly member: ShareMember
  /** The food it stands for, or null where it is a quick entry. */
  readonly food: Food | null
  /** True where a reference could not be read at all. */
  readonly missing: boolean
}

/**
 * A share and the foods its references named, read together. Every reference is asked for at
 * once — a meal of ten library foods is one request, not ten — and the page waits on the whole
 * of it, since a meal drawn from half its members is a meal saying the wrong total.
 */
export async function readShared(
  shared: Shared,
  lookUp: (refs: readonly string[]) => Promise<(Food | null)[]>,
): Promise<ReadMember[]> {
  const refs = referencesOf(shared)
  const found = refs.length === 0 ? [] : await lookUp(refs)
  const byRef = new Map<string, Food | null>()
  refs.forEach((reference, index) => {
    byRef.set(reference, found[index] ?? null)
  })
  return membersOf(shared).map((member) => {
    if (member.r !== undefined) {
      const food = byRef.get(member.r) ?? null
      return { member, food, missing: food === null }
    }
    if (member.c !== undefined) {
      return { member, food: foodFromShare(member.c), missing: false }
    }
    return { member, food: null, missing: false }
  })
}

/** What one read member comes to, in the amount the link offers it in. */
export function memberNutrients(read: ReadMember): Nutrients {
  if (read.food === null) {
    return nutrientsFromShare(read.member.nu ?? [0])
  }
  return (
    nutrientsFor(read.food, amountFromShare(read.member.a)) ??
    nutrientsFromShare(read.member.nu ?? [0])
  )
}

/** The members of a share, as the ingredients an app already knows how to draw. */
export function ingredientsOf(read: readonly ReadMember[]): Ingredient[] {
  return read.map((each) => ({
    name: each.food?.name ?? each.member.n ?? 'Food',
    food: each.food,
    amount: amountFromShare(each.member.a),
    nutrients: memberNutrients(each),
  }))
}

/** What a whole share comes to: its members together. */
export function sharedTotal(read: readonly ReadMember[]): Nutrients {
  return summed(read.map(memberNutrients))
}

/**
 * The meal a shared meal or an entry share stands for, which belongs to nobody until it is
 * kept — and which an entry share is never kept as at all: it is the page's shape, not its
 * meaning.
 */
export function mealFromShared(shared: Shared, read: readonly ReadMember[]): Meal {
  const members = ingredientsOf(read)
  // A share carries the members flat, so each stands as a slot of its own.
  const slots = members.map((member, at) => ({
    id: `shared-${String(at)}`,
    alternatives: [{ id: `shared-${String(at)}a`, member }],
  }))
  return {
    id: '',
    name: shared.n,
    emoji: null,
    slots,
    state: EMPTY_MEAL_STATE,
    members,
    logs: [],
    logCount: 0,
    lastLoggedAt: null,
  }
}

/** The recipe a shared recipe stands for, worked out the way the app works one out. */
export function recipeFromShared(shared: Shared, read: readonly ReadMember[]): Recipe | null {
  return shared.k === 'recipe'
    ? { servings: shared.sv, ingredients: ingredientsOf(read), totalGrams: shared.tw ?? null }
    : null
}

/** What a shared meal comes to together, for the head of its page. */
export function mealComesTo(meal: Meal): Nutrients {
  return mealTotal(membersOn(meal.slots, meal.state))
}

/* ---- Keeping what arrived. ---- */

/**
 * The foods a link offers to put in a library: the ones that travelled whole. A library food
 * is left out — everybody is offered it already, and there is nothing to keep.
 */
export function keepableOf(read: readonly ReadMember[]): ReadMember[] {
  return read.filter((each) => each.member.c !== undefined && each.food !== null)
}

function folded(value: string | null): string {
  return (value ?? '').trim().toLocaleLowerCase()
}

/**
 * Whether a food that arrived is one the account already holds — the nutrition spec, Keeping a
 * food already held.
 *
 * Deliberately not a content hash. The whole reason to recognise anything is the correction —
 * somebody fixed a number and sent it again — and a correction *is* a change in the numbers,
 * so a key that includes them can only ever catch an exact re-send, which is the one case
 * where there is nothing to do. What is compared is therefore what survives an edit: a
 * barcode, which really does name a product, and otherwise the name, the brand and the
 * reference amount together.
 *
 * A barcode on one side and none on the other is not a match. One of them is a packet somebody
 * scanned and the other is a thing somebody typed, and they are not known to be the same.
 */
export function sameFoodAs(
  mine: Pick<Food, 'name' | 'brand' | 'barcode' | 'reference'>,
  theirs: Pick<Food, 'name' | 'brand' | 'barcode' | 'reference'>,
): boolean {
  const here = folded(mine.barcode)
  const there = folded(theirs.barcode)
  if (here !== '' || there !== '') {
    /*
     * The reference amount counts even under a matching barcode. Taking the new figures writes
     * them onto the held food while keeping the held food's own reference, so one row stated
     * per 100 g and another per portion would silently reinterpret every number.
     */
    return here !== '' && here === there && mine.reference === theirs.reference
  }
  return (
    folded(mine.name) === folded(theirs.name) &&
    folded(mine.brand) === folded(theirs.brand) &&
    mine.reference === theirs.reference
  )
}

/** The one the account already holds that a food arriving is, or null where it holds none. */
export function alreadyHeld(theirs: Food, mine: readonly Food[]): Food | null {
  return mine.find((each) => sameFoodAs(each, theirs)) ?? null
}

/** A nutrient the two disagree about, as the dialog says it. */
export interface NutrientChange {
  readonly nutrient: Nutrient
  readonly label: string
  readonly from: number | null
  readonly to: number | null
}

/**
 * What the food arriving says differently from the one already held — the lines the dialog
 * shows before offering to take the new figures. Empty where the two agree, which is what
 * turns the dialog from a correction into a plain "you have this already".
 */
export function nutrientChanges(mine: Nutrients, theirs: Nutrients): NutrientChange[] {
  return NUTRIENTS.filter(
    (nutrient) => (mine[nutrient] ?? null) !== (theirs[nutrient] ?? null),
  ).map((nutrient) => ({
    nutrient,
    label: NUTRIENT_LABELS[nutrient],
    from: mine[nutrient] ?? null,
    to: theirs[nutrient] ?? null,
  }))
}
